import React, { useState, useEffect } from 'react';
import styles from '../Blog/AIWriting.module.css';

const TONES = [
  'Professional & Persuasive',
  'Casual & Friendly',
  'Informative & Technical',
  'Luxury & Premium',
  'Fun & Energetic',
];

const LANGUAGES = ['English', 'Bangla', 'English + Bangla Mix'];

const SECTIONS = [
  { value: 'all', label: 'All Content' },
  { value: 'description', label: 'Description Only' },
  { value: 'seo', label: 'SEO Only' },
  { value: 'faqs', label: 'FAQs Only' },
  { value: 'tags', label: 'Tags Only' },
];

function AIProductModal({ open, onClose, onGenerated, isEditMode, existingData }) {
  const [productName, setProductName] = useState('');
  const [keyFeatures, setKeyFeatures] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState(TONES[0]);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [section, setSection] = useState('all');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ tokens: 0, length: 0 });
  const [error, setError] = useState('');
  const [stage, setStage] = useState('');

  // Pre-fill from existing data in edit mode
  useEffect(() => {
    if (open && existingData) {
      if (existingData.productName) setProductName(existingData.productName);
    }
  }, [open, existingData]);

  const handleGenerate = async () => {
    if (!productName.trim()) { setError('Please enter a product name'); return; }
    setError('');
    setGenerating(true);
    setProgress({ tokens: 0, length: 0 });
    setStage('Connecting to AI...');

    const token = localStorage.getItem('token');
    const uid = localStorage.getItem('id');

    try {
      const res = await fetch('https://api.ecomtechbd.com/product/ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token, uid },
        body: JSON.stringify({
          productName: productName.trim(),
          keyFeatures: keyFeatures.trim(),
          targetAudience: targetAudience.trim(),
          tone,
          language,
          section,
        }),
      });

      if (!res.ok) throw new Error('Server error');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      setStage('AI is analyzing product details...');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'progress') {
              setProgress({ tokens: data.tokens, length: data.length });
              if (data.tokens < 30) setStage('AI is analyzing product details...');
              else if (data.tokens < 100) setStage('Writing product description...');
              else if (data.tokens < 250) setStage('Crafting compelling copy...');
              else if (data.tokens < 400) setStage('Generating SEO metadata...');
              else if (data.tokens < 600) setStage('Creating FAQs...');
              else setStage('Finalizing content...');
            }

            if (data.type === 'complete') {
              if (data.success) {
                setStage('Done!');
                setTimeout(() => {
                  onGenerated(data.data, data.stats, data.section);
                  setGenerating(false);
                  if (!isEditMode) {
                    setProductName('');
                    setKeyFeatures('');
                    setTargetAudience('');
                  }
                  setProgress({ tokens: 0, length: 0 });
                  setStage('');
                }, 500);
              } else {
                setError(data.message || 'Generation failed. Please try again.');
                setGenerating(false);
                setStage('');
              }
            }
          } catch (e) {}
        }
      }
    } catch (err) {
      setError('Failed to connect: ' + err.message);
      setGenerating(false);
      setStage('');
    }
  };

  const handleGenerateAltText = async () => {
    if (!existingData?.images?.length) {
      setError('No images uploaded yet. Upload images first.');
      return;
    }

    const imagesWithUrl = existingData.images.filter(img => img.url);
    if (!imagesWithUrl.length) {
      setError('No images with URLs found.');
      return;
    }

    setError('');
    setGenerating(true);
    setStage('Generating alt text for images...');
    setProgress({ tokens: 0, length: 0 });

    const token = localStorage.getItem('token');
    const uid = localStorage.getItem('id');

    try {
      const res = await fetch('https://api.ecomtechbd.com/product/ai-alt-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token, uid },
        body: JSON.stringify({
          images: imagesWithUrl.map((img, i) => ({ url: img.url, index: i })),
          productName: productName || existingData?.productName || 'Product',
        }),
      });

      if (!res.ok) throw new Error('Server error');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'progress') {
              setStage(`Analyzing image ${data.current} of ${data.total}...`);
              setProgress({ tokens: data.current, length: data.total });
            }

            if (data.type === 'complete') {
              if (data.success) {
                setStage('Done!');
                setTimeout(() => {
                  onGenerated({ altTexts: data.altTexts }, null, 'altText');
                  setGenerating(false);
                  setProgress({ tokens: 0, length: 0 });
                  setStage('');
                }, 500);
              } else {
                setError(data.message || 'Alt text generation failed.');
                setGenerating(false);
                setStage('');
              }
            }
          } catch (e) {}
        }
      }
    } catch (err) {
      setError('Failed to connect: ' + err.message);
      setGenerating(false);
      setStage('');
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={() => !generating && onClose()}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <span className={styles.aiIcon}>AI</span>
            <h2 className={styles.modalTitle}>AI Product Writer</h2>
          </div>
          {!generating && (
            <button className={styles.closeBtn} onClick={onClose}>&times;</button>
          )}
        </div>

        {!generating ? (
          <div className={styles.modalBody}>
            <p className={styles.modalDesc}>
              Enter your product details and AI will generate description, SEO content, FAQs, and tags.
            </p>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Product Name *</label>
              <input
                className={styles.fieldInput}
                value={productName}
                onChange={e => { setProductName(e.target.value); setError(''); }}
                placeholder="e.g. Luxury Portable Women Bag"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Key Features</label>
              <textarea
                className={styles.fieldTextarea}
                rows={3}
                value={keyFeatures}
                onChange={e => setKeyFeatures(e.target.value)}
                placeholder="PU leather, compartmentalized interior, dual carry options, available in Khaki and Coffee Brown"
              />
              <span className={styles.fieldHint}>Comma separated or bullet points</span>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Target Audience</label>
                <input
                  className={styles.fieldInput}
                  value={targetAudience}
                  onChange={e => setTargetAudience(e.target.value)}
                  placeholder="e.g. Modern women in Bangladesh"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Tone</label>
                <select className={styles.fieldSelect} value={tone} onChange={e => setTone(e.target.value)}>
                  {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Language</label>
                <select className={styles.fieldSelect} value={language} onChange={e => setLanguage(e.target.value)}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              {isEditMode && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Generate</label>
                  <select className={styles.fieldSelect} value={section} onChange={e => setSection(e.target.value)}>
                    {SECTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <div className={styles.modalFooter}>
              {isEditMode && existingData?.images?.some(img => img.url) && (
                <button className={styles.cancelBtn} onClick={handleGenerateAltText} style={{borderColor: '#6366f1', color: '#6366f1'}}>
                  Generate Alt Text
                </button>
              )}
              <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button className={styles.generateBtn} onClick={handleGenerate}>
                Generate Content
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <div className={styles.progressWrap}>
              <div className={styles.progressSpinner}>
                <div className={styles.spinnerRing}></div>
                <span className={styles.spinnerIcon}>AI</span>
              </div>
              <h3 className={styles.progressTitle}>{stage}</h3>
              <div className={styles.progressStats}>
                <span>Tokens: {progress.tokens}</span>
                <span>|</span>
                <span>Characters: {progress.length.toLocaleString()}</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${Math.min(100, (progress.tokens / 8))}%` }}
                />
              </div>
              <p className={styles.progressHint}>
                This may take 1-3 minutes. Please don't close this window.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIProductModal;
